// assetStatus.ts — the `products.asset_status` vocabulary, in one place.
//
// Why this module exists
// ──────────────────────
// Eleven admin surfaces tested `asset_status === "complete"` directly to mean
// "AR ready". That was wrong in the direction that matters: the value written
// on the HAPPY path is `ready`, not `complete`.
//
//   process-qa-validation:755   qa_status "approved" → asset_status "ready"
//                               (+ active = true, in the same patch)
//   process-qa-validation:776   qa_status "review"   → asset_status "complete"
//                               ("review" is illegal for the column's check
//                               constraint, so the review path borrows
//                               "complete" to mean "pipeline finished")
//
// So every approved, published product rendered as "No AR" on any surface that
// compared against the literal, and the three shipped WRT6000 fireplaces —
// every product carrying `ready` in live data — were exactly those products.
// The pages that appeared to work did so only because they routed through
// `isArReady()`, which ignores asset_status entirely and infers readiness from
// asset URLs. That inference masked the bug rather than avoiding it.
//
// Dependency-free on purpose, same as pipelineStages.ts — no React, no path
// aliases — so `npx tsx src/lib/assetStatus.test.ts` just runs.

/** Every value `products_asset_status_check` permits. Confirmed against the
 *  live constraint; a write outside this set throws 23514 and silently fails
 *  the whole PATCH, taking every other field in it down too. */
export const ASSET_STATUS_VALUES = [
  "pending",
  "processing",
  "generating_texture",
  "complete",
  "failed",
  "ready",
] as const;

export type AssetStatus = typeof ASSET_STATUS_VALUES[number];

/** The states meaning the pipeline is done with this product, whichever way it
 *  came out. `ready` is the approved path, `complete` the review path. Neither
 *  will change without a human or a re-run. */
const PIPELINE_FINISHED = new Set<string>(["ready", "complete"]);

/** In-flight states. `generating_texture` is a legal constraint value that no
 *  current writer emits; folded in here so it can never read as "No AR" if
 *  something starts writing it. */
const PIPELINE_RUNNING = new Set<string>(["processing", "generating_texture"]);

/** Has the pipeline finished with this product, approved or flagged? */
export function isAssetPipelineFinished(status: string | null | undefined): boolean {
  return PIPELINE_FINISHED.has(status ?? "");
}

/**
 * Has this product been QA-APPROVED, as opposed to merely finished?
 *
 * Narrower than the above and deliberately so: `ready` is written together with
 * `active = true`, so it means published, while `complete` means "finished,
 * a human still has to look". Anything gating publication or catalogue
 * visibility wants this one. Anything asking "is the pipeline still working"
 * wants `isAssetPipelineFinished`.
 */
export function isAssetApproved(status: string | null | undefined): boolean {
  return status === "ready";
}

/** The shape every admin surface actually needs off a product row. */
export interface ArStatusBearing {
  asset_status?: string | null;
  glb_pbr_url?: string | null;
  usdz_pbr_url?: string | null;
  glb_assembly_status?: string | null;
}

/** The four display states the admin badges/dots/filters render, plus null for
 *  "nothing to say". Keep page-level label and colour maps keyed on THIS, never
 *  on a raw asset_status — that is the mistake this module exists to retire. */
export type ArDisplayStatus = "ready" | "processing" | "pending" | "failed";

/**
 * Normalises a product row to the display vocabulary.
 *
 * Order matters. asset_status is consulted first because it is what the
 * pipeline actually asserts; the URL check is a fallback for rows written
 * before asset_status was reliably terminal (it was never written at all on the
 * no-source-photos path until 2026-08-26), not a competing source of truth.
 */
export function arDisplayStatus(p: ArStatusBearing): ArDisplayStatus | null {
  const s = p.asset_status ?? "";
  if (isAssetPipelineFinished(s)) return "ready";
  if (PIPELINE_RUNNING.has(s))    return "processing";
  if (s === "pending")            return "pending";
  if (s === "failed")             return "failed";
  return hasArAssets(p) ? "ready" : null;
}

/** Do the AR model files themselves exist? A statement about artifacts, not
 *  about pipeline state — the two can legitimately disagree while a run is in
 *  flight. */
export function hasArAssets(p: ArStatusBearing): boolean {
  return !!((p.glb_pbr_url && p.glb_assembly_status === "complete") || p.usdz_pbr_url);
}
